import * as Yup from "yup";
import { formatCurrency } from "../utils/formatters";

export const subscriptionPlanInitialValues = {
  id: "",
  plan_name: "",
  plan_code: "",
  base_price: "",
  max_users: "",
  trial_days: "",
  billing_cycle: "",
  service_id: "",
  offer_text: "",
};

export const subscriptionPlanValidationSchema = Yup.object({
  plan_name: Yup.string().trim().required("Plan name is required"),
  plan_code: Yup.string().trim().required("Plan code is required"),
  base_price: Yup.number()
    .typeError("Base price must be a number")
    .min(0, "Base price cannot be negative")
    .required("Base price is required"),
  max_users: Yup.number()
    .typeError("Max users must be a number")
    .min(1, "Max users must be at least 1")
    .required("Max users is required"),
  trial_days: Yup.number()
    .typeError("Trial days must be a number")
    .min(0, "Trial days cannot be negative")
    .required("Trial days is required"),
  billing_cycle: Yup.string().trim().required("Billing cycle is required"),
  service_id: Yup.number()
    .typeError("Service is required")
    .required("Service is required"),
  offer_text: Yup.string().max(160, "Offer text should be concise"),
});

export const getSubscriptionPlanFields = (serviceOptions = []) => [
  {
    name: "plan_name",
    label: "Plan name",
    placeholder: "Starter Plan",
  },
  {
    name: "plan_code",
    label: "Plan code",
    placeholder: "STARTER_2026",
  },
  {
    name: "base_price",
    label: "Base price",
    type: "number",
    min: 0,
    step: "0.01",
    placeholder: "1499",
  },
  {
    name: "billing_cycle",
    label: "Billing cycle",
    placeholder: "Monthly, Quarterly, Yearly",
  },
  {
    name: "max_users",
    label: "Max users",
    type: "number",
    min: 1,
    step: 1,
    placeholder: "10",
  },
  {
    name: "trial_days",
    label: "Trial days",
    type: "number",
    min: 0,
    step: 1,
    placeholder: "14",
  },
  {
    name: "service_id",
    label: "Service",
    type: "select",
    placeholder: "Select a service",
    options: serviceOptions,
  },
  {
    name: "offer_text",
    label: "Offer text",
    type: "textarea",
    width: "full",
    placeholder: "Add a concise marketing line for the plan",
    hint: "Optional. This appears in the listing and details view.",
  },
];

export const mapSubscriptionPlanToFormValues = (record) => ({
  ...subscriptionPlanInitialValues,
  id: record?.id || "",
  plan_name: record?.plan_name || "",
  plan_code: record?.plan_code || "",
  base_price: record?.base_price ?? "",
  max_users: record?.max_users ?? "",
  trial_days: record?.trial_days ?? "",
  billing_cycle: record?.billing_cycle || "",
  service_id: record?.service_id || record?.service?.id || "",
  offer_text: record?.offer_text || "",
});

export const mapSubscriptionPlanToPayload = (values, existingRecord) => ({
  ...(existingRecord?.id ? { id: existingRecord.id } : {}),
  plan_name: values.plan_name.trim(),
  plan_code: values.plan_code.trim(),
  base_price: Number(values.base_price),
  max_users: Number(values.max_users),
  trial_days: Number(values.trial_days),
  billing_cycle: values.billing_cycle.trim(),
  service_id: Number(values.service_id),
  offer_text: values.offer_text?.trim() || "",
  ...(typeof existingRecord?.isActive === "boolean"
    ? { isActive: existingRecord.isActive }
    : {}),
});

export const getSubscriptionPlanDetails = (record) => [
  {
    title: "Plan Overview",
    items: [
      { label: "Plan name", value: record?.plan_name || "N/A" },
      { label: "Plan code", value: record?.plan_code || "N/A" },
      {
        label: "Base price",
        value: formatCurrency(record?.base_price),
      },
      { label: "Billing cycle", value: record?.billing_cycle || "N/A" },
      { label: "Trial days", value: `${record?.trial_days ?? 0} days` },
      { label: "Max users", value: record?.max_users ?? 0 },
      {
        label: "Offer text",
        value: record?.offer_text || "No offer text configured",
      },
      {
        label: "Status",
        value: record?.isActive ? "Active" : "Inactive",
      },
    ],
  },
  {
    title: "Service Mapping",
    items: [
      { label: "Service", value: record?.service?.name || "N/A" },
      { label: "Service ID", value: record?.service_id || record?.service?.id || "N/A" },
      {
        label: "Service status",
        value:
          typeof record?.service?.active === "boolean"
            ? record.service.active
              ? "Active"
              : "Inactive"
            : "N/A",
      },
      {
        label: "Service description",
        value: record?.service?.description || "No service description available",
      },
    ],
  },
];
